import type {
  Overview,
  Opportunity,
  Signal,
  Bet,
  BetStats,
  DailyPnL,
  CumulativePnL,
  RiskStatus,
  Config,
  Event,
  EventDetail,
  GamesResponse,
} from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '';
const TOKEN_KEY = 'rlm_auth_token';

// Token management
export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearStoredToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export async function fetchAPI<T>(endpoint: string): Promise<T> {
  const token = getStoredToken();
  const headers: Record<string, string> = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    credentials: 'include',
    headers,
  });
  if (!response.ok) {
    if (response.status === 401) {
      clearStoredToken();
      // Redirect to login on auth error
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }
    throw new Error(`API error: ${response.status}`);
  }
  return response.json();
}

// Authentication
export async function login(password: string): Promise<{ success: boolean; error?: string; token?: string }> {
  const response = await fetch(`${API_BASE}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ password }),
  });
  const data = await response.json();
  if (data.success && data.token) {
    setStoredToken(data.token);
  }
  return data;
}

export async function logout(): Promise<void> {
  const token = getStoredToken();
  const headers: Record<string, string> = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  await fetch(`${API_BASE}/api/auth/logout`, {
    method: 'POST',
    credentials: 'include',
    headers,
  });
  clearStoredToken();
}

export async function checkAuth(): Promise<boolean> {
  try {
    const token = getStoredToken();
    if (!token) {
      return false;
    }

    const response = await fetch(`${API_BASE}/api/auth/check`, {
      credentials: 'include',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    const data = await response.json();
    if (!data.authenticated) {
      clearStoredToken();
    }
    return data.authenticated === true;
  } catch {
    return false;
  }
}

// Overview
export async function fetchOverview(): Promise<Overview> {
  return fetchAPI<Overview>('/api/overview');
}

// Opportunities
export async function fetchOpportunities(params?: {
  limit?: number;
  offset?: number;
  minEdge?: number;
  market?: string;
  sport?: string;
}): Promise<{ opportunities: Opportunity[]; pagination: { limit: number; offset: number; total: number } }> {
  const searchParams = new URLSearchParams();
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.offset) searchParams.set('offset', params.offset.toString());
  if (params?.minEdge) searchParams.set('min_edge', params.minEdge.toString());
  if (params?.market) searchParams.set('market', params.market);
  if (params?.sport) searchParams.set('sport', params.sport);

  const query = searchParams.toString();
  return fetchAPI(`/api/opportunities${query ? `?${query}` : ''}`);
}

export async function fetchOpportunity(id: string): Promise<{ opportunity: Opportunity; signalDetails: Record<string, unknown> | null }> {
  return fetchAPI(`/api/opportunities/${id}`);
}

// Signals
export async function fetchSignals(params?: {
  limit?: number;
  offset?: number;
  type?: string;
  market?: string;
  sport?: string;
  minStrength?: number;
}): Promise<{ signals: Signal[]; countsByType: Record<string, number>; countsByMarket?: Record<string, number>; pagination: { limit: number; offset: number } }> {
  const searchParams = new URLSearchParams();
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.offset) searchParams.set('offset', params.offset.toString());
  if (params?.type) searchParams.set('type', params.type);
  if (params?.market) searchParams.set('market', params.market);
  if (params?.sport) searchParams.set('sport', params.sport);
  if (params?.minStrength) searchParams.set('min_strength', params.minStrength.toString());

  const query = searchParams.toString();
  return fetchAPI(`/api/signals${query ? `?${query}` : ''}`);
}

export async function fetchSignalsByType(type: string): Promise<{ type: string; signals: Signal[]; count: number }> {
  return fetchAPI(`/api/signals/${type}`);
}

// Bets
export async function fetchBets(params?: {
  limit?: number;
  offset?: number;
  status?: string;
  days?: number;
}): Promise<{ bets: Bet[]; pagination: { limit: number; offset: number; days: number } }> {
  const searchParams = new URLSearchParams();
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.offset) searchParams.set('offset', params.offset.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.days) searchParams.set('days', params.days.toString());

  const query = searchParams.toString();
  return fetchAPI(`/api/bets${query ? `?${query}` : ''}`);
}

export async function fetchBetStats(): Promise<BetStats> {
  return fetchAPI<BetStats>('/api/bets/stats');
}

// P&L
export async function fetchDailyPnL(days = 30): Promise<DailyPnL> {
  return fetchAPI<DailyPnL>(`/api/pnl/daily?days=${days}`);
}

export async function fetchCumulativePnL(): Promise<CumulativePnL> {
  return fetchAPI<CumulativePnL>('/api/pnl/cumulative');
}

// Events
export async function fetchEvents(): Promise<{ events: Event[] }> {
  return fetchAPI('/api/events');
}

// Games (grouped by event with signals)
export async function fetchGames(params?: {
  sport?: string;
  betType?: string;
  sort?: 'signal_count' | 'commence_time' | 'aggregate_strength';
  sortDir?: 'asc' | 'desc';
  minSignals?: number;
  signalsOnly?: boolean;
  limit?: number;
  offset?: number;
}): Promise<GamesResponse> {
  const searchParams = new URLSearchParams();
  if (params?.sport) searchParams.set('sport', params.sport);
  if (params?.betType) searchParams.set('bet_type', params.betType);
  if (params?.sort) searchParams.set('sort', params.sort);
  if (params?.sortDir) searchParams.set('sort_dir', params.sortDir);
  if (params?.minSignals !== undefined) searchParams.set('min_signals', params.minSignals.toString());
  if (params?.signalsOnly) searchParams.set('signals_only', 'true');
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.offset) searchParams.set('offset', params.offset.toString());

  const query = searchParams.toString();
  return fetchAPI<GamesResponse>(`/api/games${query ? `?${query}` : ''}`);
}

// Risk
export async function fetchRiskStatus(): Promise<RiskStatus> {
  return fetchAPI<RiskStatus>('/api/risk');
}

// Config
export async function fetchConfig(): Promise<Config> {
  return fetchAPI<Config>('/api/config');
}

export interface BettingConfigUpdate {
  bettingEnabled?: boolean;
  maxBetSize?: number;
  maxDailyLoss?: number;
  maxConcurrentBets?: number;
  minEdgeThreshold?: number;
  kellyFraction?: number;
  cooldownSeconds?: number;
}

export interface ConfigUpdateResponse {
  success: boolean;
  errors?: string[];
  betting?: {
    enabled: boolean;
    maxBetSize: number;
    maxDailyLoss: number;
    maxConcurrentBets: number;
    minEdgeThreshold: number;
    kellyFraction: number;
    cooldownSeconds: number;
  };
}

export async function updateConfig(updates: BettingConfigUpdate): Promise<ConfigUpdateResponse> {
  const token = getStoredToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/api/config`, {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    if (response.status === 401) {
      clearStoredToken();
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }
    throw new Error(`API error: ${response.status}`);
  }

  return response.json();
}

export async function resetConfig(): Promise<ConfigUpdateResponse> {
  const token = getStoredToken();
  const headers: Record<string, string> = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/api/config?action=reset`, {
    method: 'POST',
    credentials: 'include',
    headers,
  });

  if (!response.ok) {
    if (response.status === 401) {
      clearStoredToken();
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }
    throw new Error(`API error: ${response.status}`);
  }

  return response.json();
}

// Event Detail
export async function fetchEvent(eventId: string): Promise<EventDetail> {
  return fetchAPI<EventDetail>(`/api/events/${eventId}`);
}

// =============================================================================
// SIMPLIFIED BETTING DECISION ENDPOINTS
// =============================================================================

export interface BetScoreItem {
  eventId: string;
  homeTeam: string;
  awayTeam: string;
  sport: string;
  betScore: number;
  recommendation: 'BET' | 'CONSIDER' | 'WAIT' | 'AVOID';
  direction: string | null;
  components: {
    marketDiscrepancy: number;
    signalStrength: number;
    historical: number;
    validation: number;
  };
  hoursToEvent: number;
  scoredAt: string;
}

export interface BetScoresResponse {
  timestamp: string;
  total: number;
  scores: BetScoreItem[];
  summary: {
    betCount: number;
    considerCount: number;
    waitCount: number;
    avoidCount: number;
    bestScore: number;
  };
}

export interface PositionItem {
  eventId: string;
  homeTeam: string;
  awayTeam: string;
  health: number;
  unrealizedPnL: number;
  recommendation: 'HOLD' | 'MONITOR' | 'REDUCE' | 'EXIT';
  healthStatus: 'healthy' | 'watch' | 'reduce' | 'exit';
}

export interface PositionsResponse {
  timestamp: string;
  overallHealth: number;
  positionCount: number;
  atRiskCount: number;
  totalUnrealizedPnL: number;
  positions: PositionItem[];
  thresholds: {
    hold: number;
    monitor: number;
    reduce: number;
    exit: number;
  };
}

export interface DashboardSummary {
  timestamp: string;
  betScore: {
    score: number;
    recommendation: 'BET' | 'CONSIDER' | 'WAIT' | 'AVOID';
    event: {
      homeTeam: string;
      awayTeam: string;
      sport: string;
    };
    direction: string | null;
  } | null;
  portfolioHealth: {
    score: number;
    positionCount: number;
    atRiskCount: number;
    totalUnrealizedPnL: number;
  };
  systemStatus: 'ACTIVE' | 'CAUTION' | 'PAUSE';
  statusReason: string;
  dailyPnL: number;
  weeklyWinRate: number | null;
  actions: {
    shouldBet: boolean;
    shouldMonitor: boolean;
    bestBetEvent: string | null;
  };
}

// Bet Scores
export async function fetchBetScores(params?: { limit?: number; minScore?: number }): Promise<BetScoresResponse> {
  const searchParams = new URLSearchParams();
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.minScore) searchParams.set('min_score', params.minScore.toString());
  const query = searchParams.toString();
  return fetchAPI<BetScoresResponse>(`/api/bet-scores${query ? `?${query}` : ''}`);
}

// Positions
export async function fetchPositions(): Promise<PositionsResponse> {
  return fetchAPI<PositionsResponse>('/api/positions');
}

// Dashboard Summary (the simplified view)
export async function fetchDashboardSummary(): Promise<DashboardSummary> {
  return fetchAPI<DashboardSummary>('/api/dashboard/summary');
}

// =============================================================================
// QUOTA MONITORING
// =============================================================================

export interface QuotaResponse {
  remaining: number;
  usedToday: number;
  burnRate: number;
  lastUpdated: string;
  usedThisHour: number;
  projectedDaysRemaining: number | null;
  alertLevel: 'ok' | 'warning' | 'critical';
}

// Quota
export async function fetchQuota(): Promise<QuotaResponse> {
  return fetchAPI<QuotaResponse>('/api/quota');
}

// =============================================================================
// POLYMARKET API MONITORING
// =============================================================================

export interface PolymarketStatsResponse {
  // API usage
  gammaCallsToday: number;
  clobCallsToday: number;
  gammaCallsThisMinute: number;
  clobCallsThisMinute: number;

  // Events
  eventsTracked: number;
  liveEvents: number;
  preGameEvents: number;

  // Timing
  lastPoll: string;
  avgResponseTimeMs: number;

  // Health
  circuitBreakerStatus: 'closed' | 'open' | 'half-open';
  gammaHealthy: boolean;
  clobHealthy: boolean;
  errorCountLastHour: number;

  // Detailed circuit breaker info
  circuitBreaker: {
    gamma: {
      state: string;
      canProceed: boolean;
      cooldownRemaining?: number;
    };
    clob: {
      state: string;
      canProceed: boolean;
      cooldownRemaining?: number;
    };
  };
}

// Polymarket Stats
export async function fetchPolymarketStats(): Promise<PolymarketStatsResponse> {
  return fetchAPI<PolymarketStatsResponse>('/api/polymarket-stats');
}

// =============================================================================
// EVENT DETAIL EXTENDED DATA (for redesigned Event Detail page)
// =============================================================================

// These endpoints are being created by the backend agent
// For now, they gracefully fall back to existing data if endpoints aren't ready

export async function fetchEventPolymarketHistory(eventId: string, hours = 24) {
  try {
    return await fetchAPI(`/api/events/${eventId}/polymarket-history?hours=${hours}`);
  } catch (error) {
    // Fallback: return empty data if endpoint not ready
    console.warn('Polymarket history endpoint not ready, using fallback');
    return { priceHistory: [] };
  }
}

export async function fetchEventOddsHistory(eventId: string, hours = 24) {
  try {
    return await fetchAPI(`/api/events/${eventId}/odds-history?hours=${hours}`);
  } catch (error) {
    // Fallback: return empty data if endpoint not ready
    console.warn('Odds history endpoint not ready, using fallback');
    return { oddsHistory: [] };
  }
}

export async function fetchEventMetrics(eventId: string) {
  try {
    return await fetchAPI(`/api/events/${eventId}/metrics`);
  } catch (error) {
    // Fallback: return null metrics if endpoint not ready
    console.warn('Event metrics endpoint not ready, using fallback');
    return {
      polymarketPrice: null,
      polymarketChange24h: null,
      lastOddsUpdate: null,
      priceGap: null,
      freshnessStatus: 'stale' as const,
    };
  }
}

// =============================================================================
// LIFECYCLE & POLLING TIER MONITORING
// =============================================================================

export interface LifecycleStatsResponse {
  timestamp: string;
  timezone: string;
  stages: {
    scheduled: number;
    pre_game: number;
    live: number;
    completed: number;
  };
  totalTracked: number;
  historicalData: {
    oddsApi: {
      eventsWithHistory: number;
      totalEvents: number;
      percentage: number;
    };
    polymarket: {
      eventsWithHistory: number;
      totalEvents: number;
      percentage: number;
    };
  };
  liveDetection: {
    totalLive: number;
    byConfidence: Record<'high' | 'medium' | 'low', number>;
    byReason: Record<string, number>;
  };
  pollingTiers: {
    urgent: number;
    soon: number;
    later: number;
    live: number;
    skip: number;
  };
}

export interface EventLifecycleResponse {
  eventId: string;
  lifecycle: {
    currentStage: 'scheduled' | 'pre_game' | 'live' | 'completed';
    commenceTime: string;
    estimatedEndTime: string;
    historicalDownloaded: boolean;
    pollIntervalSeconds: number;
    lastPolled: string;
    liveDetection?: {
      detectedAt?: string;
      confidence?: string;
      reason?: string;
    };
  } | null;
  pollingTier: {
    name: string;
    intervalSeconds: number;
    description: string;
  };
}

// Lifecycle Stats
export async function fetchLifecycleStats(): Promise<LifecycleStatsResponse> {
  return fetchAPI<LifecycleStatsResponse>('/api/lifecycle-stats');
}

// Event Lifecycle
export async function fetchEventLifecycle(eventId: string): Promise<EventLifecycleResponse> {
  try {
    return await fetchAPI<EventLifecycleResponse>(`/api/events/${eventId}/lifecycle`);
  } catch (error) {
    console.warn('Event lifecycle endpoint not ready, using fallback');
    return {
      eventId,
      lifecycle: null,
      pollingTier: {
        name: 'unknown',
        intervalSeconds: 0,
        description: 'Lifecycle data unavailable',
      },
    };
  }
}

// Betting Volume/Flow Data
export interface BettingVolumeResponse {
  eventId: string;
  homeTeam: string;
  awayTeam: string;
  publicBetting: {
    source: string;
    ticketPctHome: number;
    ticketPctAway: number;
    moneyPctHome: number;
    moneyPctAway: number;
    sharpMoneyIndicator: number;
    lastUpdate: string;
  } | null;
  markets: Array<{
    marketType: string;
    bookmakerCount: number;
    outcomes: Array<{
      name: string;
      bestOdds: number;
      bestBook: string;
      avgOdds: number;
      impliedProb: number;
      point?: number | null;
      bookmakers: Array<{
        name: string;
        price: number;
        isSharp: boolean;
      }>;
    }>;
  }>;
  summary: {
    totalBookmakers: number;
    sharpBookCount: number;
    squareBookCount: number;
    marketCoverage: Record<string, number>;
    sharpVsSquareDivergence: {
      home: number;
      away: number;
    } | null;
  };
  lineMovement: {
    openingLine: { home: number; away: number } | null;
    currentLine: { home: number; away: number } | null;
    movement: { home: number; away: number } | null;
  };
}

export async function fetchBettingVolume(eventId: string): Promise<BettingVolumeResponse | null> {
  try {
    return await fetchAPI<BettingVolumeResponse>(`/api/events/${eventId}/betting-volume`);
  } catch (error) {
    console.warn('Betting volume endpoint not ready:', error);
    return null;
  }
}
